import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dueLabel } from '../lib/date';
import {
  useMyAssignments,
  useCompleteAssignment,
  useSkipAssignment,
  useFreshStart,
  useShareAssignment,
  useCancelShare,
  useAcceptShare,
  useDeclineShare,
  useIncomingShares,
} from '../hooks/useChoreAssignments';
import { Modal } from '../components/Modal';
import { BreakSheet } from '../components/BreakSheet';
import { MemberAvatar } from '../components/MemberAvatar';
import {
  useAllHouseholdMembers,
  useAllHouseholdSettings,
  useHouseholdMembers,
} from '../hooks/useHousehold';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import { useAllChoreDefinitions } from '../hooks/useChoreDefinitions';
import { useAllRooms } from '../hooks/useRooms';
import { useAuth } from '../hooks/useAuth';
import { useActiveMember } from '../hooks/useActiveMember';
import { useAllWeeklyDailyStats } from '../hooks/useUserPoints';
import { PointsWidget } from '../components/PointsWidget';
import { ChoreTile } from '../components/ChoreTile';
import { SwipeCoachMark } from '../components/SwipeCoachMark';
import { useCelebration } from '../components/Celebration';
import { useNotificationPreferences, useTogglePush } from '../hooks/usePush';
import { useMyProfile } from '../hooks/useUserProfile';
import { pushSupported } from '../lib/push';
import { updateWidget } from '../lib/widget';
import { tapLight } from '../lib/haptics';
import { calcPoints, computeMax, streakLength, isGoalHit, isStale } from '../lib/dashboard';
import { PRIORITY_META, LENGTH_META } from '../lib/chore-meta';
import { AssignmentStatus, ChoreLength, ChorePriority, daysBetween, type ChoreAssignment } from '@chore-project/shared';

type Assignment = ChoreAssignment;

/** Warm time-of-day salutation for the dashboard header. */
function greetingFor(hour: number): string {
  if (hour < 5) return 'Up late';
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  return 'Evening';
}

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { householdId: firstHouseholdId, households, isLoading: householdsLoading } = useSelectedHousehold();
  const { data: assignments, isLoading } = useMyAssignments();
  const complete = useCompleteAssignment();
  const skip = useSkipAssignment();
  const share = useShareAssignment();
  const cancelShare = useCancelShare();
  const acceptShare = useAcceptShare();
  const declineShare = useDeclineShare();
  const { data: incoming } = useIncomingShares();
  const householdIds = (households ?? []).map((h) => h.householdId);
  // The dashboard is user-scoped: assignments span every household the user
  // belongs to, so rooms, members and settings are fetched across all of them.
  const { data: settingsById } = useAllHouseholdSettings(householdIds);
  const { data: defs, isLoading: defsLoading } = useAllChoreDefinitions(householdIds);
  const { data: rooms } = useAllRooms(householdIds);
  const { days, data: weekStats, isLoading: statsLoading } = useAllWeeklyDailyStats(householdIds);
  const { data: members } = useAllHouseholdMembers(householdIds);
  // The "me" the dashboard reflects: the shared user switched into, or the signed-in user.
  const { activeMember } = useActiveMember(user?.sub ?? '');
  const meId = activeMember?.userId ?? user?.sub;
  const hasPersonalised = !!(members?.find((m) => m.userId === meId)?.avatarColor);
  const { celebrate, tada } = useCelebration();
  const { data: pushPrefs } = useNotificationPreferences();
  const togglePush = useTogglePush();
  const pushOn = pushPrefs?.pushEnabled ?? false;
  const freshStart = useFreshStart();
  const [freshStartOpen, setFreshStartOpen] = useState(false);
  // A break is a personal (signed-in user) state; hidden while acting as a shared user.
  const { data: profile } = useMyProfile();
  const currentBreak = activeMember ? undefined : profile?.break;
  const [breakOpen, setBreakOpen] = useState(false);

  const [completing, setCompleting] = useState<Set<string>>(new Set());
  const [skipping, setSkipping] = useState<Set<string>>(new Set());
  const [sharing, setSharing] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState<Set<string>>(new Set());
  // The chore whose secondary-actions (⋯) sheet is open, and the chore being passed.
  const [menuFor, setMenuFor] = useState<Assignment | null>(null);
  const [passFor, setPassFor] = useState<Assignment | null>(null);
  const { data: pickerMembers } = useHouseholdMembers(passFor?.householdId ?? '');
  const [doneOpen, setDoneOpen] = useState(true);
  const [whatNextDismissed, setWhatNextDismissed] = useState(
    () => localStorage.getItem('onboarding-what-next-dismissed') === 'true',
  );

  const handleComplete = (a: Assignment) => {
    if (completing.has(a.assignmentId) || complete.isPending) return;
    tada();
    setCompleting((prev) => new Set([...prev, a.assignmentId]));
    setTimeout(() => {
      void complete.mutate({ householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId });
    }, 380);
  };

  const handleSkip = (a: Assignment) => {
    if (skipping.has(a.assignmentId) || skip.isPending) return;
    setSkipping((prev) => new Set([...prev, a.assignmentId]));
    void skip.mutate({ householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId });
  };

  const removeFrom = (setter: typeof setSharing, id: string) =>
    setter((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // Pass the currently-picked chore to the chosen member.
  const handlePassTo = (recipientId: string) => {
    const a = passFor;
    if (!a) return;
    tapLight();
    setPassFor(null);
    setSharing((prev) => new Set([...prev, a.assignmentId]));
    share.mutate(
      { householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId, recipientId },
      { onSettled: () => removeFrom(setSharing, a.assignmentId) },
    );
  };

  const handleCancelShare = (a: Assignment) => {
    if (resolving.has(a.assignmentId)) return;
    setResolving((prev) => new Set([...prev, a.assignmentId]));
    cancelShare.mutate(
      { householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId },
      { onSettled: () => removeFrom(setResolving, a.assignmentId) },
    );
  };

  const handleAcceptShare = (a: Assignment) => {
    if (resolving.has(a.assignmentId)) return;
    tada();
    setResolving((prev) => new Set([...prev, a.assignmentId]));
    acceptShare.mutate(
      { householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId },
      { onSettled: () => removeFrom(setResolving, a.assignmentId) },
    );
  };

  const handleDeclineShare = (a: Assignment) => {
    if (resolving.has(a.assignmentId)) return;
    setResolving((prev) => new Set([...prev, a.assignmentId]));
    declineShare.mutate(
      { householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId },
      { onSettled: () => removeFrom(setResolving, a.assignmentId) },
    );
  };

  // Resolve a member's display name within the household members we know about.
  const nameOf = (userId?: string) => {
    if (!userId) return 'a housemate';
    const m = members?.find((mm) => mm.userId === userId);
    return m?.memberName || m?.email?.split('@')[0] || 'a housemate';
  };

  const getDef = (choreDefId: string) => defs?.find((d) => d.choreDefId === choreDefId);

  const today = new Date().toISOString().split('T')[0] ?? '';

  const pending = assignments?.filter((a) => a.status === AssignmentStatus.PENDING) ?? [];
  const overdue = pending.filter((a) => a.dueDate < today);
  const upcoming = pending.filter((a) => a.dueDate >= today);
  // Chores I've passed to a housemate, awaiting their response (still owned by me).
  const sharedOut = assignments?.filter((a) => a.status === AssignmentStatus.SHARED) ?? [];
  const incomingShares = incoming ?? [];

  // Derived from the single /me/assignments fetch (which returns all statuses) —
  // no separate "completed today" request needed.
  const completedToday = (assignments ?? []).filter(
    (a) => a.status === AssignmentStatus.COMPLETED && a.dueDate === today,
  );

  const todayAssignments = [
    ...pending.filter((a) => a.dueDate === today),
    ...completedToday,
  ];

  const weeklyData = days.map((date) => {
    const userStat = weekStats.find((s) => s.date === date && s.userId === meId);
    const isToday = date === today;
    const dateAssignments = isToday ? todayAssignments : (assignments?.filter((a) => a.dueDate === date) ?? []);
    const computedMax = computeMax(dateAssignments);
    return {
      date,
      points: userStat?.dailyPoints ?? 0,
      maxPoints: Math.max(computedMax, userStat?.maxPoints ?? 0),
    };
  });
  const earnedToday = weeklyData[6]?.points ?? 0;
  // Derive today's max from the weekly data, which floors the live (race-prone)
  // assignment recompute against the server's recorded daily max — so the ring
  // doesn't flicker while the pending/completed lists refetch separately.
  const maxToday = weeklyData[6]?.maxPoints ?? 0;

  // True consecutive-day streak, from the member's persisted start/last-active
  // dates. Across households we show the user's best current run (and best ever).
  const myMemberRows = (members ?? []).filter((m) => m.userId === meId);
  const streak = Math.max(0, ...myMemberRows.map((m) => streakLength(m.streakStartDate, m.streakLastDate)));
  const bestStreak = Math.max(0, ...myMemberRows.map((m) => m.streakLongest ?? 0));

  // Big celebration the moment the daily goal is first reached (once per day).
  const goalHit = isGoalHit(earnedToday, maxToday);
  const celebratedGoal = useRef(false);
  useEffect(() => {
    const key = `goal-celebrated-${today}`;
    if (goalHit && !celebratedGoal.current && sessionStorage.getItem(key) !== 'true') {
      celebratedGoal.current = true;
      sessionStorage.setItem(key, 'true');
      celebrate({ icon: 'trophy', title: 'Daily goal smashed!', sub: `${earnedToday} points earned today` });
    }
    if (!goalHit) celebratedGoal.current = false;
  }, [goalHit, earnedToday, today, celebrate]);

  // Mirror today's status onto the Android home-screen widget. Counts only —
  // no chore specifics. No-op off Android.
  const todoToday = pending.filter((a) => a.dueDate === today).length;
  const skippedToday = (assignments ?? []).filter(
    (a) => a.status === AssignmentStatus.MISSED && a.dueDate === today,
  ).length;
  const doneToday = completedToday.length;
  const overdueCount = overdue.length;
  useEffect(() => {
    if (isLoading || statsLoading) return;
    void updateWidget({
      earned: earnedToday,
      max: maxToday,
      todo: todoToday,
      done: doneToday,
      skipped: skippedToday,
      overdue: overdueCount,
      streak,
    });
  }, [earnedToday, maxToday, todoToday, doneToday, skippedToday, overdueCount, streak, isLoading, statsLoading]);

  // Onboarding state: 0 = no household, 1 = no defs, 2 = no completed chores, null = all done
  const onboardingReady = !householdsLoading && !defsLoading && !statsLoading;
  const hasHousehold = !!firstHouseholdId;
  const hasNoDefs = defs !== undefined && defs.length === 0;
  const hasDoneChore = earnedToday > 0 || weeklyData.some((d) => d.points > 0);
  const setupStep: number | null = !hasHousehold ? 0 : hasNoDefs ? 1 : !hasDoneChore ? 2 : null;

  // "Stale" returning user: set up & active before, but away for a real stretch
  // (measured from their last actual completion, across any household) and now
  // carrying a meaningful backlog of overdue chores. These are the folks the
  // fresh-start reset is for (distinct from brand-new users in the setup flow).
  const lastActive = myMemberRows
    .map((m) => m.streakLastDate)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);
  const daysAway = lastActive ? daysBetween(lastActive, today) : Infinity;
  const stale = onboardingReady && isStale({ hasHousehold, hasNoDefs, daysAway, overdueCount: overdue.length });

  // Offer the fresh start automatically when stale, unless recently dismissed.
  const freshStartPrompted = useRef(false);
  useEffect(() => {
    if (!stale || freshStartPrompted.current) return;
    const dismissedAt = localStorage.getItem('fresh-start-dismissed-at');
    if (dismissedAt) {
      const daysSince = (Date.now() - new Date(dismissedAt).getTime()) / 86_400_000;
      if (daysSince < 7) return; // respect a 7-day cooldown
    }
    freshStartPrompted.current = true;
    setFreshStartOpen(true);
  }, [stale]);

  const dismissFreshStart = () => {
    localStorage.setItem('fresh-start-dismissed-at', new Date().toISOString());
    setFreshStartOpen(false);
  };

  const handleFreshStart = async () => {
    try {
      await freshStart.mutateAsync();
      localStorage.removeItem('fresh-start-dismissed-at');
      setFreshStartOpen(false);
      tada();
    } catch {
      // Leave the modal open; the error is surfaced inline below.
    }
  };

  const roomMap = new Map(rooms.map((r) => [r.roomId, r.name]));

  function groupByRoom(items: Assignment[]): Array<{ roomLabel: string; items: Assignment[] }> {
    const groups = new Map<string, Assignment[]>();
    for (const a of items) {
      const roomId = getDef(a.choreDefId)?.roomId;
      const key = roomId && roomMap.has(roomId) ? roomId : '';
      const group = groups.get(key) ?? [];
      group.push(a);
      groups.set(key, group);
    }
    const result: Array<{ roomLabel: string; items: Assignment[] }> = [];
    const namedKeys = [...groups.keys()]
      .filter((k) => k !== '')
      .sort((a, b) => (roomMap.get(a) ?? '').localeCompare(roomMap.get(b) ?? ''));
    for (const key of namedKeys) {
      result.push({ roomLabel: roomMap.get(key)!, items: groups.get(key)! });
    }
    if (groups.has('')) result.push({ roomLabel: 'Other', items: groups.get('')! });
    return result;
  }

  // Width and stagger delay vary per bar; everything else lives in the class.
  const skeletonBar = (width: string, delay = '0s') => (
    <div className="skeleton-bar" style={{ width, animationDelay: delay }} />
  );

  const renderSkeletonRows = (count = 4) =>
    Array.from({ length: count }, (_, i) => (
      <div key={i} className="chore-tile">
        <div className="chore-tile-inner">
          {skeletonBar(`${58 + (i % 3) * 12}%`, `${i * 80}ms`)}
          {skeletonBar('36%', `${i * 80 + 40}ms`)}
          <div className="chore-tile-foot">
            <div className="skeleton-lines">
              {skeletonBar('55%', `${i * 80 + 80}ms`)}
            </div>
            <div className="skeleton-circle" style={{ animationDelay: `${i * 80 + 120}ms` }} />
          </div>
        </div>
      </div>
    ));

  const renderPendingTile = (a: Assignment, overdueRow = false, roomLabel?: string) => {
    const def = getDef(a.choreDefId);
    const priority = a.priority ?? def?.priority ?? ChorePriority.SHOULD_DO;
    const length = a.length ?? def?.length ?? ChoreLength.MEDIUM;
    const pts = calcPoints(priority, length);
    const due = dueLabel(a.dueDate);
    // Skip permission follows the assignment's own household, not the selected one.
    const allowSkip = settingsById.get(a.householdId)?.allowSkipping !== false;
    const busy = completing.has(a.assignmentId) || skipping.has(a.assignmentId) || sharing.has(a.assignmentId);
    return (
      <ChoreTile
        key={a.assignmentId}
        title={a.choreName}
        priority={priority}
        room={roomLabel}
        points={pts}
        overdue={overdueRow}
        completing={completing.has(a.assignmentId)}
        disabled={busy || complete.isPending}
        onComplete={() => handleComplete(a)}
        onSkip={allowSkip ? () => handleSkip(a) : undefined}
        onOpen={() => { tapLight(); setMenuFor(a); }}
        meta={
          <>
            <span>{LENGTH_META[length].label}</span>
            <span className={due.late ? 'chore-due-late' : undefined}>{due.text}</span>
          </>
        }
      />
    );
  };

  // Room-sorted, but flat: the room reads as a small-caps label on each
  // tile rather than a detached group heading.
  const renderRoomGroups = (items: Assignment[], overdueBadge = false) => {
    if (rooms.length === 0) return items.map((a) => renderPendingTile(a, overdueBadge));
    return groupByRoom(items).flatMap(({ roomLabel, items: groupItems }) =>
      groupItems.map((a) => renderPendingTile(a, overdueBadge, roomLabel === 'Other' ? undefined : roomLabel)),
    );
  };

  const renderSetupGuide = (step: number) => {
    const steps = [
      { title: 'Create a household', desc: 'Invite your housemates and manage chores together', to: '/household' },
      { title: 'Define your chores', desc: 'Add the recurring tasks your household needs doing', to: '/chore-definitions' },
      { title: 'Complete your first chore', desc: 'Mark a task done and earn points on your daily ring', to: null },
    ];
    return (
      <div className="onboard-card">
        <div className="onboard-head">
          <span className="section-label">Getting started</span>
          <span className="onboard-count">{step} of 3 done</span>
        </div>
        {steps.map(({ title, desc, to }, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <div
              key={title}
              className={`onboard-row${done ? ' onboard-row--done' : ''}${active ? ' onboard-row--active' : ''}`}
            >
              <div className={`onboard-step${done ? ' onboard-step--done' : active ? ' onboard-step--active' : ''}`}>
                {done ? '✓' : i + 1}
              </div>
              <div className="onboard-row-body">
                <div className="onboard-row-title">{title}</div>
                {!done && <div className="onboard-row-desc">{desc}</div>}
              </div>
              {active && to && (
                <Link to={to} className="btn btn-sm btn-primary">Set up →</Link>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWhatNext = () => {
    const items: Array<{ icon: string; title: string; desc: string; to?: string; onClick?: () => void }> = [
      // Push is the primary's device setting — not offered while acting as a shared user.
      ...(pushSupported() && !pushOn && !activeMember ? [{ icon: 'notifications-outline', title: 'Turn on notifications', desc: 'A gentle morning briefing, evening reminders, and a cheer when housemates finish.', onClick: () => togglePush.mutate(true) }] : []),
      ...(!hasPersonalised ? [{ icon: 'person-circle-outline', title: 'Personalise your avatar', desc: 'Set a name, colour and icon for your household profile so housemates can tell you apart.', to: '/household' }] : []),
      { icon: 'grid-outline', title: 'Organise by room', desc: 'Group chores by space — Kitchen, Bathroom, Garden — for a cleaner view.', to: '/rooms' },
      { icon: 'add-circle-outline', title: 'One-off assignments', desc: 'Need something done outside the schedule? Create a manual assignment anytime.', to: '/assignments' },
    ];
    return (
    <div className="onboard-card">
      <div className="onboard-head">
        <span className="section-label">What's next?</span>
        <button
          className="onboard-dismiss"
          onClick={() => { localStorage.setItem('onboarding-what-next-dismissed', 'true'); setWhatNextDismissed(true); }}
          aria-label="Dismiss"
        >
          <ion-icon name="close-outline" />
        </button>
      </div>
      {items.map(({ icon, title, desc, to, onClick }) => (
        <div key={title} className="onboard-row">
          <ion-icon name={icon} className="onboard-row-icon" />
          <div className="onboard-row-body">
            <div className="onboard-row-title">{title}</div>
            <div className="onboard-row-desc">{desc}</div>
          </div>
          {to ? (
            <Link to={to} className="btn btn-sm btn-ghost">Explore →</Link>
          ) : (
            <button className="btn btn-sm btn-primary" onClick={onClick} disabled={togglePush.isPending}>
              {togglePush.isPending ? '…' : 'Turn on'}
            </button>
          )}
        </div>
      ))}
    </div>
    );
  };

  // A chore a housemate has passed to me — Accept (become owner) or Decline
  // (send back). Standard tile: the points show what accepting earns.
  const renderIncomingRow = (a: Assignment) => {
    const busy = resolving.has(a.assignmentId);
    const due = dueLabel(a.dueDate);
    const def = getDef(a.choreDefId);
    const priority = a.priority ?? def?.priority ?? ChorePriority.SHOULD_DO;
    const length = a.length ?? def?.length ?? ChoreLength.MEDIUM;
    const roomLabel = def?.roomId ? roomMap.get(def.roomId) : undefined;
    return (
      <ChoreTile
        key={a.assignmentId}
        title={a.choreName}
        icon="hand-right"
        points={calcPoints(priority, length)}
        room={roomLabel}
        meta={
          <>
            <span>from {nameOf(a.sharedBy)}</span>
            <span className={due.late ? 'chore-due-late' : undefined}>{due.text}</span>
          </>
        }
        actions={
          <>
            <button className="btn btn-sm btn-ghost" onClick={() => handleDeclineShare(a)} disabled={busy}>
              Decline
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => handleAcceptShare(a)} disabled={busy}>
              Accept
            </button>
          </>
        }
      />
    );
  };

  // A chore I've passed out, waiting on the recipient — read-only with a Cancel option.
  const renderWaitingRow = (a: Assignment) => {
    const busy = resolving.has(a.assignmentId);
    const due = dueLabel(a.dueDate);
    const def = getDef(a.choreDefId);
    const priority = a.priority ?? def?.priority ?? ChorePriority.SHOULD_DO;
    const length = a.length ?? def?.length ?? ChoreLength.MEDIUM;
    const roomLabel = def?.roomId ? roomMap.get(def.roomId) : undefined;
    return (
      <ChoreTile
        key={a.assignmentId}
        title={a.choreName}
        icon="hourglass-outline"
        points={calcPoints(priority, length)}
        room={roomLabel}
        meta={
          <>
            <span>Passed to {nameOf(a.sharedWith)}</span>
            <span className={due.late ? 'chore-due-late' : undefined}>{due.text}</span>
          </>
        }
        actions={
          <button className="btn btn-sm btn-ghost" onClick={() => handleCancelShare(a)} disabled={busy}>
            Cancel
          </button>
        }
      />
    );
  };

  const otherMembers = (pickerMembers ?? []).filter((m) => m.userId !== meId);

  // Warm greeting: the shared user switched into, my member profile, or my email.
  const me = members?.find((m) => m.userId === meId);
  const firstName = (activeMember?.memberName || me?.memberName || user?.email?.split('@')[0] || '')
    .trim()
    .split(/\s+/)[0];
  const dateLine = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  const breakReturnLabel = currentBreak
    ? (() => {
        const [y, m, d] = currentBreak.returnDate.split('-').map(Number);
        return new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(y!, m! - 1, d!));
      })()
    : '';

  // The day's peak moment: nothing left to do (and nothing in limbo).
  const allClear =
    onboardingReady && !isLoading && setupStep === null && !stale &&
    pending.length === 0 && incomingShares.length === 0 && sharedOut.length === 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="greeting-kicker">{dateLine}</p>
          <h1>{greetingFor(new Date().getHours())}{firstName ? `, ${firstName}` : ''}</h1>
        </div>
      </div>

      {currentBreak && (
        <button className="break-cta" onClick={() => { tapLight(); setBreakOpen(true); }}>
          <span className="break-cta-icon"><ion-icon name="sunny" /></span>
          <span className="sheet-row-text">
            <span className="break-cta-title">You’re on a break 🌴</span>
            <span className="break-cta-sub">Back on {breakReturnLabel} · ready to dive back in?</span>
          </span>
          <span className="break-cta-action">End break</span>
        </button>
      )}

      <PointsWidget
        earnedToday={earnedToday}
        maxToday={maxToday}
        streak={streak}
        bestStreak={bestStreak}
        weeklyData={weeklyData}
        loading={householdsLoading || isLoading || statsLoading}
      />

      {goalHit && (
        <button
          className="share-prompt"
          onClick={() => {
            tapLight();
            navigate('/share', {
              state: { earned: earnedToday, max: maxToday, done: completedToday.length, streak, date: today },
            });
          }}
        >
          <span className="share-prompt-icon"><ion-icon name="share-social" /></span>
          <span className="sheet-row-text">
            <span className="share-prompt-title">Day done — share your win? 🎉</span>
            <span className="share-prompt-sub">Turn today into a card for your socials</span>
          </span>
          <ion-icon name="chevron-forward" className="sheet-row-chevron" />
        </button>
      )}

      {onboardingReady && setupStep !== null && !stale && renderSetupGuide(setupStep)}
      {onboardingReady && setupStep === null && !whatNextDismissed && renderWhatNext()}

      {allClear && (
        <div className="all-clear">
          <div className="all-clear-icon" aria-hidden="true">
            <ion-icon name={completedToday.length > 0 ? 'checkmark-done' : 'cafe-outline'} />
          </div>
          <div className="all-clear-title">
            {completedToday.length > 0 ? 'All done for today!' : 'Nothing on your plate'}
          </div>
          <div className="all-clear-sub">
            {completedToday.length > 0
              ? `${completedToday.length} chore${completedToday.length === 1 ? '' : 's'} finished — go put your feet up.`
              : 'No chores due today. Enjoy the breather ☕'}
          </div>
        </div>
      )}

      {/* First-run gesture hint, shown once there's a real chore to swipe. */}
      {onboardingReady && setupStep === null && !isLoading && pending.length > 0 && <SwipeCoachMark />}

      {incomingShares.length > 0 && (
        <section>
          <p className="section-label section-label--primary">
            Passed to you — {incomingShares.length}
          </p>
          <div className="chore-tiles">{incomingShares.map(renderIncomingRow)}</div>
        </section>
      )}

      {!isLoading && overdue.length > 0 && (
        <section>
          <p className="section-label section-label--danger">
            Overdue — {overdue.length}
          </p>
          <div className="chore-tiles">{renderRoomGroups(overdue, true)}</div>
        </section>
      )}

      {isLoading ? (
        <section>
          <p className="section-label">To do</p>
          <div className="chore-tiles">{renderSkeletonRows()}</div>
        </section>
      ) : upcoming.length > 0 ? (
        <section>
          <p className="section-label">To do — {upcoming.length}</p>
          <div className="chore-tiles">{renderRoomGroups(upcoming)}</div>
        </section>
      ) : null}

      {sharedOut.length > 0 && (
        <section>
          <p className="section-label">Waiting on a housemate — {sharedOut.length}</p>
          <div className="chore-tiles">{sharedOut.map(renderWaitingRow)}</div>
        </section>
      )}

      {completedToday.length > 0 && (
        <div>
          <button
            className="section-label disclosure-summary"
            onClick={() => setDoneOpen((o) => !o)}
            aria-expanded={doneOpen}
          >
            <ion-icon name="chevron-down-outline" className="disclosure-chevron" />
            Done today · {completedToday.length}
          </button>
          {doneOpen && <div className="chore-tiles disclosure-body">
          {completedToday.map((a) => {
            const def = getDef(a.choreDefId);
            const priority = a.priority ?? def?.priority ?? ChorePriority.SHOULD_DO;
            const length = a.length ?? def?.length ?? ChoreLength.MEDIUM;
            const pts = calcPoints(priority, length);
            const roomLabel = def?.roomId ? roomMap.get(def.roomId) : undefined;
            return (
              <ChoreTile
                key={a.assignmentId}
                title={a.choreName}
                priority={priority}
                room={roomLabel}
                points={pts}
                done
                meta={<span>{LENGTH_META[length].label}</span>}
              />
            );
          })}
          </div>}
        </div>
      )}

      {menuFor && (() => {
        const def = getDef(menuFor.choreDefId);
        const priority = menuFor.priority ?? def?.priority ?? ChorePriority.SHOULD_DO;
        const length = menuFor.length ?? def?.length ?? ChoreLength.MEDIUM;
        const pts = calcPoints(priority, length);
        const due = dueLabel(menuFor.dueDate);
        const pMeta = PRIORITY_META[priority];
        return (
        <Modal title={menuFor.choreName} onClose={() => setMenuFor(null)}>
          {def?.description && <p className="chore-sheet-desc">{def.description}</p>}
          <p className="chore-meta chore-sheet-meta">
            <span className="chore-meta-item">
              <ion-icon name={pMeta.icon} style={{ color: pMeta.color }} />
              {pMeta.label} do
            </span>
            <span className="chore-meta-item">
              <ion-icon name="timer-outline" />
              {LENGTH_META[length].label}
            </span>
            <span className={due.late ? 'chore-due-late' : undefined}>{due.text}</span>
          </p>
          <div className="sheet-stack">
            <button
              className="sheet-row"
              onClick={() => { const a = menuFor; setMenuFor(null); handleComplete(a); }}
            >
              <span className="sheet-row-icon sheet-row-icon-success"><ion-icon name="checkmark" /></span>
              <span className="sheet-row-text">
                <span className="sheet-row-title">Done!</span>
                <span className="sheet-row-sub">Mark it complete and earn +{pts}</span>
              </span>
            </button>
            <button
              className="sheet-row"
              onClick={() => { const a = menuFor; setMenuFor(null); setPassFor(a); }}
            >
              <span className="sheet-row-icon"><ion-icon name="person-add" /></span>
              <span className="sheet-row-text">
                <span className="sheet-row-title">Pass to a housemate</span>
                <span className="sheet-row-sub">Hand this chore to someone else</span>
              </span>
              <ion-icon name="chevron-forward" className="sheet-row-chevron" />
            </button>
            {settingsById.get(menuFor.householdId)?.allowSkipping !== false && (
              <button
                className="sheet-row"
                onClick={() => { const a = menuFor; setMenuFor(null); handleSkip(a); }}
              >
                <span className="sheet-row-icon"><ion-icon name="play-skip-forward" /></span>
                <span className="sheet-row-text">
                  <span className="sheet-row-title">Skip</span>
                  <span className="sheet-row-sub">Mark as missed for today</span>
                </span>
              </button>
            )}
          </div>
        </Modal>
        );
      })()}

      {passFor && (
        <Modal title="Pass to…" onClose={() => setPassFor(null)}>
          {otherMembers.length === 0 ? (
            <p className="modal-note">
              No other members to pass to. Invite a housemate from the Household page first.
            </p>
          ) : (
            <div className="sheet-stack">
              {otherMembers.map((m) => (
                <button key={m.userId} className="sheet-row" onClick={() => handlePassTo(m.userId)}>
                  <MemberAvatar member={m} size="2.25rem" fontSize="0.9rem" />
                  <span className="sheet-row-text">
                    <span className="sheet-row-title">{m.memberName || m.email}</span>
                  </span>
                  <ion-icon name="chevron-forward" className="sheet-row-chevron" />
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}

      {freshStartOpen && (
        <Modal title="Welcome back 👋" onClose={dismissFreshStart}>
          <p className="modal-note">
            It&apos;s been a little while, and {overdue.length} chore{overdue.length === 1 ? '' : 's'} piled up while you were away.
            No stress — want a clean slate?
          </p>

          <div className="perk-list">
            {[
              { icon: 'sparkles', text: 'Clears your overdue backlog — nothing left hanging' },
              { icon: 'today-outline', text: "Sets up today's chores fresh, as if you were all caught up" },
              { icon: 'happy-outline', text: 'No penalties, no guilt — just a stress-free restart' },
            ].map((row) => (
              <div key={row.text} className="perk-row">
                <span className="sheet-row-icon">
                  <ion-icon name={row.icon} />
                </span>
                <span>{row.text}</span>
              </div>
            ))}
          </div>

          {freshStart.isError && (
            <div className="alert alert-error">Couldn&apos;t reset just now. Please try again.</div>
          )}

          <div className="sheet-stack">
            <button
              className="btn btn-primary btn-full"
              onClick={() => void handleFreshStart()}
              disabled={freshStart.isPending}
            >
              {freshStart.isPending ? 'Setting up your fresh start…' : 'Give me a fresh start'}
            </button>
            <button className="btn btn-ghost btn-full" onClick={dismissFreshStart} disabled={freshStart.isPending}>
              No thanks, keep things as they are
            </button>
          </div>
        </Modal>
      )}

      {breakOpen && currentBreak && (
        <BreakSheet currentBreak={currentBreak} onClose={() => setBreakOpen(false)} />
      )}
    </div>
  );
}
